import React, { useState, useEffect } from 'react';
import { 
  FiPieChart, FiBarChart2, FiShare2, FiGrid, FiUsers, 
  FiAward, FiEdit3, FiLogOut, FiRefreshCw, FiShield, FiPlus, 
  FiX, FiTrash2, FiCamera
} from 'react-icons/fi';
import { QRCodeSVG } from 'qrcode.react';

// Firebase Firestore
import { doc, getDoc, updateDoc } from 'firebase/firestore';
import { db } from '../../firebase';

const CompetitorDashboard = ({ currentUser, onLogout, onNavigateToAdmin }) => {
  const userId = currentUser?.uid || currentUser?.id || null;

  // --- ÁLLAPOTOK ---
  const [loading, setLoading] = useState(true);
  const [userData, setUserData] = useState({
    fullName: currentUser?.displayName || "Íjász",
    club: "Egyesület: -",
    license: "Engedélysz.: -",
    profileImageUrl: currentUser?.photoURL || null,
    totalShots: 0,
    trophies: [],
    isAdmin: false
  });

  const [chartMode, setChartMode] = useState('classic');
  const [activeTab, setActiveTab] = useState('chart');
  const [showManualModal, setShowManualModal] = useState(false);
  const [manualInputValue, setManualInputValue] = useState('');
  const [selectedPieSlice, setSelectedPieSlice] = useState(null);

  const [chartStats, setChartStats] = useState({ classic: [], all: [], totalProcessed: 0 });

  // --- ADATBETÖLTÉS A FIRESTORE-BÓL ---
  useEffect(() => {
    let isMounted = true;

    const fetchDashboardData = async () => {
      if (!userId) {
        if (isMounted) setLoading(false);
        return;
      }

      try {
        const userDocRef = doc(db, 'users', userId);
        const userSnap = await getDoc(userDocRef);

        if (userSnap.exists() && isMounted) {
          const uData = userSnap.data() || {};
          const adminUids = ["c4sTvlqVBwZIQUSbqHA56MOw9wH3", "1o1A3CmOmJWqxv5TR0INePU70iP2"];

          const lastName = uData.lastName || '';
          const firstName = uData.firstName || '';
          const fullName = `${lastName} ${firstName}`.trim() || currentUser?.displayName || "Névtelen Íjász";

          // Trófeák kezelése
          const rawTrophies = Array.isArray(uData.selectedTrophies) ? uData.selectedTrophies : [];

          setUserData({
            fullName: fullName,
            club: uData.club ? `Egyesület: ${uData.club}` : "Egyesület: -",
            license: uData.license ? `Engedélysz.: ${uData.license}` : "Engedélysz.: -",
            profileImageUrl: uData.profileImageUrl || currentUser?.photoURL || null,
            totalShots: typeof uData.totalShots === 'number' ? uData.totalShots : 0,
            trophies: rawTrophies,
            isAdmin: adminUids.includes(userId)
          });

          // Kördiagram adatok
          const fullStats = uData.fullStats && typeof uData.fullStats === 'object' ? uData.fullStats : {};
          const classicMap = {};
          const allMap = {};
          let totalCount = 0;

          Object.keys(fullStats).forEach(key => {
            const count = parseInt(fullStats[key], 10) || 0;
            totalCount += count;
            allMap[key] = count;

            if (key !== 'X' && ['11', '10', '8', '5', '0'].includes(key)) {
              classicMap[key] = count;
            }
          });

          const getColor = (label) => {
            switch (label) {
              case '12':
              case 'Extra': return '#7B1FA2';
              case 'X':
              case '11': return '#FFD700';
              case '10': return '#FFEB3B';
              case '8':  return '#F44336';
              case '5':  return '#1976D2';
              case '0':  return '#9E9E9E';
              default:   return '#4CAF50';
            }
          };

          const formatSlices = (map) => Object.keys(map).map(label => ({
            name: label === '12' ? '⭐' : label,
            value: map[label],
            color: getColor(label)
          }));

          setChartStats({
            classic: formatSlices(classicMap),
            all: formatSlices(allMap),
            totalProcessed: totalCount
          });
        }
      } catch (error) {
        console.error("Hiba az adatok lekérésekor:", error);
      } finally {
        if (isMounted) setLoading(false);
      }
    };

    fetchDashboardData();
    return () => { isMounted = false; };
  }, [userId]);

  // --- KOTLIN APP-PAL EGYEZŐ XOR TITKOSÍTÓ LOGIKA A QR KÓDHOZ ---
  const xorCipher = (data) => {
    const key = "ARCHERY_NEXUS_2024_SAFE_KEY";
    let output = "";
    for (let i = 0; i < data.length; i++) {
      output += String.fromCharCode(data.charCodeAt(i) ^ key.charCodeAt(i % key.length));
    }
    return output;
  };

  const getEncryptedQrString = () => {
    if (!userId) return "";
    const rawData = `ANX|${userId}|${userData.fullName}`;
    const ciphered = xorCipher(rawData);
    // Base64 kódolás böngészőben (btoa)
    return btoa(unescape(encodeURIComponent(ciphered)));
  };

  // --- MANUÁLIS LÖVÉSSZÁM MENTÉSE ---
  const handleManualSave = async () => {
    const val = parseInt(manualInputValue, 10);
    if (!isNaN(val) && val >= 0 && userId) {
      try {
        const userRef = doc(db, 'users', userId);
        await updateDoc(userRef, { totalShots: val });
        setUserData(prev => ({ ...prev, totalShots: val }));
        setShowManualModal(false);
      } catch (err) {
        console.error("Hiba a mentéskor:", err);
      }
    }
  };

  const safeTotalShots = Math.max(0, parseInt(userData.totalShots || 0, 10));
  const formattedShots = String(safeTotalShots).padStart(10, '0').split('');

  const renderCustomPie = () => {
    const currentData = chartStats[chartMode] || [];
    const total = currentData.reduce((acc, slice) => acc + (slice.value || 0), 0);
    if (total === 0) return <p style={{ color: '#888', fontSize: '13px', margin: '20px 0' }}>Nincs még felvitt lövésadat a felhőben.</p>;

    let cumulativeAngle = 0;

    return (
      <svg width="220" height="220" viewBox="-1.1 -1.1 2.2 2.2" style={{ transform: 'rotate(-90deg)' }}>
        {currentData.map((slice, index) => {
          const sliceAngle = (slice.value / total) * 2 * Math.PI;
          const x1 = Math.cos(cumulativeAngle);
          const y1 = Math.sin(cumulativeAngle);
          const x2 = Math.cos(cumulativeAngle + sliceAngle);
          const y2 = Math.sin(cumulativeAngle + sliceAngle);
          const largeArcFlag = sliceAngle > Math.PI ? 1 : 0;

          const pathData = `M 0 0 L ${x1} ${y1} A 1 1 0 ${largeArcFlag} 1 ${x2} ${y2} Z`;
          cumulativeAngle += sliceAngle;

          return (
            <path
              key={index}
              d={pathData}
              fill={slice.color}
              stroke="#ffffff"
              strokeWidth="0.02"
              style={{ cursor: 'pointer', transition: '0.2s' }}
              onClick={() => setSelectedPieSlice(slice)}
            />
          );
        })}
        <circle cx="0" cy="0" r="0.6" fill="#ffffff" />
      </svg>
    );
  };

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: '60px 20px', color: '#666', fontFamily: 'Arial' }}>
        <h3>Adatok betöltése a felhőből... 🏹</h3>
      </div>
    );
  }

  return (
    <div style={containerStyle}>
      <div style={headerCardStyle}>
        <div style={coverImageStyle}>
          <div style={pinnedTrophiesBadge}>
            <span style={{ fontSize: '11px', color: '#aaa', fontWeight: 'bold', display: 'block', marginBottom: '4px' }}>
              KITŰZÖTT TRÓFEÁK
            </span>
            <div style={{ display: 'flex', gap: '8px' }}>
              {userData.trophies && userData.trophies.length > 0 ? (
                userData.trophies.map((tr, i) => (
                  <div key={i} style={trophyBadgeStyle}>🏆 {String(tr).replace('trophy_', '').replace(/_/g, ' ')}</div>
                ))
              ) : (
                <span style={{ fontSize: '11px', color: '#888' }}>Nincs kitűzött trófea</span>
              )}
            </div>
          </div>

          <div style={topActionsContainer}>
            <button style={actionBtnStyle} title="Profil szerkesztése">
              <FiEdit3 size={16} />
            </button>
            <button style={actionBtnStyle} title="Adatok átvitele">
              <FiRefreshCw size={16} />
            </button>
            {userData.isAdmin && (
              <button 
                style={{ ...actionBtnStyle, backgroundColor: '#2E7D32', color: 'white' }} 
                onClick={onNavigateToAdmin}
                title="Versenyiroda Admin Panel"
              >
                <FiShield size={16} />
              </button>
            )}
            <button style={{ ...actionBtnStyle, backgroundColor: '#d32f2f', color: 'white' }} onClick={onLogout} title="Kijelentkezés">
              <FiLogOut size={16} />
            </button>
          </div>
        </div>

        <div style={profileInfoRow}>
          <div style={profileImageWrapper}>
            <img 
              src={userData.profileImageUrl || "https://via.placeholder.com/120"} 
              alt="Profil" 
              style={profileImageStyle} 
            />
            <button style={cameraBtnStyle} title="Profilkép módosítása">
              <FiCamera size={14} color="#fff" />
            </button>
          </div>

          <div style={userDetailsStyle}>
            <h1 style={{ margin: 0, fontSize: '24px', color: '#fff', textShadow: '0 2px 4px rgba(0,0,0,0.5)' }}>
              {userData.fullName}
            </h1>
            <p style={{ margin: '4px 0 0 0', color: '#ddd', fontSize: '14px', fontStyle: 'italic' }}>
              {userData.club}
            </p>
            <p style={{ margin: '2px 0 0 0', color: '#bbb', fontSize: '13px' }}>
              {userData.license}
            </p>
          </div>
        </div>
      </div>

      <div style={counterCardStyle} onClick={() => { setManualInputValue(userData.totalShots); setShowManualModal(true); }}>
        <div style={counterTitleStyle}>
          <FiEdit3 size={14} /> <span>ÖSSZES LÖVÉS (KATTINTS A MÓDOSÍTÁSHOZ)</span>
        </div>
        
        <div style={clickerContainer}>
          {formattedShots.map((digit, idx) => (
            <div key={idx} style={clickerDigitBox}>
              {digit}
            </div>
          ))}
        </div>

        <div style={quickNavGrid}>
          <button style={activeTab === 'chart' ? quickNavBtnActive : quickNavBtn} onClick={(e) => { e.stopPropagation(); setActiveTab('chart'); }}>
            <FiPieChart size={20} color="#D32F2F" />
            <span>Kördiagram</span>
          </button>
          <button style={quickNavBtn} onClick={(e) => { e.stopPropagation(); alert('Részletes statisztika megnyitása...'); }}>
            <FiBarChart2 size={20} color="#D32F2F" />
            <span>Összesített</span>
          </button>
          <button style={activeTab === 'shared' ? quickNavBtnActive : quickNavBtn} onClick={(e) => { e.stopPropagation(); setActiveTab('shared'); }}>
            <FiShare2 size={20} color="#D32F2F" />
            <span>Megosztások</span>
          </button>
          <button style={activeTab === 'qr' ? quickNavBtnActive : quickNavBtn} onClick={(e) => { e.stopPropagation(); setActiveTab('qr'); }}>
            <FiGrid size={20} color="#D32F2F" />
            <span>QR Kód</span>
          </button>
          <button style={quickNavBtn} onClick={(e) => { e.stopPropagation(); alert('Közösségi modul megnyitása...'); }}>
            <FiUsers size={20} color="#D32F2F" />
            <span>Közösség</span>
          </button>
          <button style={quickNavBtn} onClick={(e) => { e.stopPropagation(); alert('Trófeák megnyitása...'); }}>
            <FiAward size={20} color="#D32F2F" />
            <span>Trófeák</span>
          </button>
        </div>
      </div>

      {activeTab === 'chart' && (
        <div style={contentCardStyle}>
          <div style={contentCardHeader}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <FiPieChart size={18} color="#D32F2F" />
              <h3 style={{ margin: 0, fontSize: '16px', color: '#333' }}>Lövések eloszlása</h3>
            </div>
            <div style={switchContainerStyle}>
              <span style={{ fontSize: '12px', color: '#666', fontWeight: 'bold' }}>
                {chartMode === 'classic' ? 'Klasszikus nézet' : 'Összes lövés'}
              </span>
              <button 
                style={{ ...switchToggleStyle, backgroundColor: chartMode === 'classic' ? '#D32F2F' : '#4CAF50' }}
                onClick={() => setChartMode(chartMode === 'classic' ? 'all' : 'classic')}
              >
                <div style={{ ...switchKnobStyle, transform: chartMode === 'classic' ? 'translateX(0)' : 'translateX(20px)' }} />
              </button>
            </div>
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', marginTop: '20px', position: 'relative' }}>
            {renderCustomPie()}
            {chartStats[chartMode]?.length > 0 && (
              <div style={pieCenterTextStyle}>
                <span style={{ fontSize: '22px', fontWeight: 'bold', color: '#333' }}>
                  {selectedPieSlice ? selectedPieSlice.value : 'Stat'}
                </span>
                <span style={{ fontSize: '11px', color: '#888' }}>
                  {selectedPieSlice ? selectedPieSlice.name : 'Kattints egy szeletre'}
                </span>
              </div>
            )}
            <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', justifyContent: 'center', marginTop: '16px' }}>
              {(chartStats[chartMode] || []).map((slice, idx) => (
                <div key={idx} style={{ display: 'flex', alignItems: 'center', gap: '4px', fontSize: '12px', cursor: 'pointer' }} onClick={() => setSelectedPieSlice(slice)}>
                  <div style={{ width: '12px', height: '12px', borderRadius: '3px', backgroundColor: slice.color }} />
                  <span><strong>{slice.name}:</strong> {slice.value}</span>
                </div>
              ))}
            </div>
          </div>
          <p style={{ textAlign: 'center', color: '#666', fontSize: '13px', marginTop: '16px' }}>
            Összesen <strong>{chartStats.totalProcessed}</strong> feldolgozott lövés alapján
          </p>
        </div>
      )}

      {activeTab === 'qr' && (
        <div style={{ ...contentCardStyle, textAlign: 'center' }}>
          <h3 style={{ margin: '0 0 8px 0', fontSize: '16px', color: '#333' }}>Saját QR Azonosító</h3>
          <p style={{ fontSize: '13px', color: '#666', margin: '0 0 20px 0' }}>
            Ezzel tudnak a versenyirodában regisztrálni, barátnak jelölni vagy csoporthoz adni.
          </p>
          <div style={{ display: 'inline-block', padding: '16px', backgroundColor: '#f9f9f9', borderRadius: '12px', border: '1px solid #eee' }}>
            <QRCodeSVG value={getEncryptedQrString()} size={180} />
          </div>
        </div>
      )}

      {showManualModal && (
        <div style={modalOverlayStyle}>
          <div style={modalContentStyle}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
              <h3 style={{ margin: 0, color: '#b30000', fontSize: '16px' }}>Összes lövés módosítása</h3>
              <button style={{ border: 'none', background: 'none', cursor: 'pointer' }} onClick={() => setShowManualModal(false)}><FiX size={20} /></button>
            </div>
            <input type="number" value={manualInputValue} onChange={(e) => setManualInputValue(e.target.value)} style={modalInputStyle} />
            <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end', marginTop: '16px' }}>
              <button style={cancelBtnStyle} onClick={() => setShowManualModal(false)}>Mégse</button>
              <button style={saveBtnStyle} onClick={handleManualSave}>Mentés</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// --- STÍLUSOK ---
const containerStyle = { maxWidth: '900px', margin: '0 auto', padding: '20px 16px 40px 16px', fontFamily: 'Arial, sans-serif', backgroundColor: '#f4f5f7', minHeight: '100vh' };
const headerCardStyle = { backgroundColor: '#1e1e1e', borderRadius: '16px', overflow: 'hidden', boxShadow: '0 8px 20px rgba(0,0,0,0.15)', marginBottom: '16px' };
const coverImageStyle = { height: '160px', backgroundImage: 'linear-gradient(to bottom, rgba(0,0,0,0.3), rgba(0,0,0,0.7)), url("https://images.unsplash.com/photo-1511067007398-7e4b90cfa4bc?auto=format&fit=crop&w=1000&q=80")', backgroundSize: 'cover', backgroundPosition: 'center', padding: '16px', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' };
const pinnedTrophiesBadge = { backgroundColor: 'rgba(0,0,0,0.6)', backdropFilter: 'blur(4px)', padding: '8px 12px', borderRadius: '8px', border: '1px solid rgba(255,255,255,0.1)' };
const trophyBadgeStyle = { backgroundColor: 'rgba(212, 175, 55, 0.2)', color: '#FFD700', fontSize: '11px', padding: '3px 8px', borderRadius: '4px', border: '1px solid #FFD700', fontWeight: 'bold', textTransform: 'capitalize' };
const topActionsContainer = { display: 'flex', gap: '8px' };
const actionBtnStyle = { width: '36px', height: '36px', borderRadius: '50%', border: 'none', backgroundColor: 'rgba(255,255,255,0.9)', color: '#333', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', boxShadow: '0 2px 6px rgba(0,0,0,0.2)' };
const profileInfoRow = { padding: '0 24px 20px 24px', display: 'flex', alignItems: 'flex-end', gap: '20px', marginTop: '-40px' };
const profileImageWrapper = { position: 'relative' };
const profileImageStyle = { width: '100px', height: '100px', borderRadius: '50%', border: '4px solid #1e1e1e', objectFit: 'cover', backgroundColor: '#fff', boxShadow: '0 4px 10px rgba(0,0,0,0.3)' };
const cameraBtnStyle = { position: 'absolute', bottom: '4px', right: '4px', backgroundColor: '#D32F2F', border: '2px solid #fff', borderRadius: '50%', width: '28px', height: '28px', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' };
const userDetailsStyle = { marginBottom: '8px' };
const counterCardStyle = { backgroundColor: '#fff', borderRadius: '16px', padding: '16px 20px', boxShadow: '0 4px 12px rgba(0,0,0,0.06)', marginBottom: '16px', cursor: 'pointer' };
const counterTitleStyle = { display: 'flex', alignItems: 'center', gap: '6px', fontSize: '11px', fontWeight: 'bold', color: '#888', marginBottom: '12px' };
const clickerContainer = { display: 'flex', justifyContent: 'center', gap: '4px', backgroundColor: '#333', padding: '8px', borderRadius: '8px', boxShadow: 'inset 0 2px 6px rgba(0,0,0,0.5)', marginBottom: '16px' };
const clickerDigitBox = { width: '28px', height: '40px', backgroundColor: '#111', color: '#fff', borderRadius: '4px', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '20px', fontWeight: 'bold', border: '1px solid #444', boxShadow: '0 2px 4px rgba(0,0,0,0.4)' };
const quickNavGrid = { display: 'grid', gridTemplateColumns: 'repeat(6, 1fr)', gap: '8px' };
const quickNavBtn = { display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '4px', padding: '10px 4px', border: 'none', backgroundColor: 'transparent', borderRadius: '8px', cursor: 'pointer', fontSize: '11px', color: '#555' };
const quickNavBtnActive = { ...quickNavBtn, backgroundColor: '#ffebee', color: '#D32F2F', fontWeight: 'bold' };
const contentCardStyle = { backgroundColor: '#fff', borderRadius: '16px', padding: '20px', boxShadow: '0 4px 12px rgba(0,0,0,0.06)', marginBottom: '16px' };
const contentCardHeader = { display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid #eee', paddingBottom: '12px' };
const switchContainerStyle = { display: 'flex', alignItems: 'center', gap: '8px' };
const switchToggleStyle = { width: '42px', height: '22px', borderRadius: '12px', border: 'none', padding: '2px', cursor: 'pointer', position: 'relative', transition: '0.3s' };
const switchKnobStyle = { width: '18px', height: '18px', backgroundColor: '#fff', borderRadius: '50%', transition: '0.3s' };
const pieCenterTextStyle = { position: 'absolute', top: '110px', left: '50%', transform: 'translate(-50%, -50%)', display: 'flex', flexDirection: 'column', alignItems: 'center', pointerEvents: 'none' };
const modalOverlayStyle = { position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 };
const modalContentStyle = { backgroundColor: '#fff', borderRadius: '12px', padding: '20px', width: '320px', boxShadow: '0 10px 25px rgba(0,0,0,0.2)' };
const modalInputStyle = { width: '100%', padding: '10px', fontSize: '16px', borderRadius: '6px', border: '1px solid #ccc', boxSizing: 'border-box' };
const cancelBtnStyle = { padding: '8px 14px', border: 'none', backgroundColor: '#eee', borderRadius: '6px', cursor: 'pointer' };
const saveBtnStyle = { padding: '8px 14px', border: 'none', backgroundColor: '#b30000', color: '#fff', borderRadius: '6px', cursor: 'pointer', fontWeight: 'bold' };

export default CompetitorDashboard;
